import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { useMount } from '@provider-portal/common/hooks/useMount';

import {
  credentialsAuthAsync,
  logoutAsync,
  NetworkRequestState,
  selectAuthRequestState,
  selectIsUserLoggedIn,
} from '@bolteu/redux-session';

import { selectLogoutRequestState } from 'rdx/session/selectors';

import { LoginUrlParams } from 'screens/Login/Login';

import type { CredentialsWithMagicLink } from 'root/rdx/session/createCredentialsSessionEpicDependencies';

/**
 * There is no magic link authentication in @bolteu/redux-session
 * CredentialsWithMagicLink is how we authenticate across different methods
 */
function getCredentials(token: string): Required<CredentialsWithMagicLink> {
  return {
    token,
    // Falsy string works in both the typing requirements and auth logic
    username: '',
    password: '',
  };
}

export function LoginWithToken() {
  const history = useHistory();

  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const authRequestState = useSelector(selectAuthRequestState);
  const logoutRequestState = useSelector(selectLogoutRequestState);
  const dispatch = useDispatch();
  const { token } = useParams<LoginUrlParams>();

  // authRequestState is global Redux state, so this guard survives this component unmounting and
  // remounting (e.g. AppContainer's isUserInitializationFinished gate rebuilding the route tree
  // right after this auth succeeds, before the account-info fetch has caught up). Dispatching
  // credentialsAuthAsync.request a second time would reuse the same, already-consumed magic-link
  // token and fail.
  const dispatchCredentialsAuth = useCallback(() => {
    if (authRequestState !== NetworkRequestState.unset) {
      return;
    }
    dispatch(credentialsAuthAsync.request(getCredentials(token)));
  }, [authRequestState, dispatch, token]);

  useMount(() => {
    if (authRequestState === NetworkRequestState.unset) {
      if (isUserLoggedIn) {
        dispatch(logoutAsync.request({ shouldResetToken: true }));
      }

      if (!isUserLoggedIn && logoutRequestState === NetworkRequestState.unset) {
        dispatchCredentialsAuth();
      }
    }
  });
  useEffect(() => {
    if (
      logoutRequestState === NetworkRequestState.success ||
      logoutRequestState === NetworkRequestState.failure
    ) {
      dispatchCredentialsAuth();
    }
  }, [logoutRequestState, authRequestState, dispatchCredentialsAuth]);

  useEffect(() => {
    if (
      authRequestState === NetworkRequestState.success ||
      authRequestState === NetworkRequestState.failure
    ) {
      history.push('/');
    }
  });

  return null;
}
